/**
 * Pure merge functions for sync conflicts (multi-device, multi-tab, or a
 * 412 rev race). Designed so that no merge can silently lose data:
 *
 *  - transcripts are unioned by message id — messages only ever accumulate;
 *  - index entries are unioned by id, newest `updatedAt` wins per session;
 *  - explicit deletions win over stale content via tombstones, but activity
 *    AFTER a deletion resurrects the session (correct for "deleted on one
 *    device, kept chatting on another").
 */

import type {
  SyncFolderLike,
  SyncIndexDoc,
  SyncMessageLike,
  SyncSessionLike,
  Tombstone,
} from './types';

/**
 * How long tombstones are retained before being garbage-collected at merge
 * time. Must comfortably exceed the longest plausible offline period of a
 * device that might re-upload a stale index.
 */
export const TOMBSTONE_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * Union two transcripts by message id, ordered by timestamp (id as the
 * tiebreaker so the result is deterministic). On id collision the local
 * copy wins — it's the one the user is looking at — with one exception: a
 * local copy marked `truncated` (a half-answer this client force-closed when
 * the socket went away) loses to a whole remote one. Callers that diff the
 * result must compare CONTENT, not just length: that exception swaps a
 * message without changing the count.
 */
export function mergeMessages<M extends SyncMessageLike>(
  local: readonly M[],
  remote: readonly M[],
): readonly M[] {
  const byId = new Map<string, M>();
  for (const m of remote) byId.set(m.id, m);
  for (const m of local) {
    // Local wins, EXCEPT where the local copy is a half-answer this client
    // force-closed when the socket went away and the remote one is whole.
    // Local-wins exists to protect edits made here from a stale server copy;
    // a truncated bubble is the opposite — the server's copy is the better
    // one, and without this the partial permanently displaces it.
    const existing = byId.get(m.id);
    if (
      m.truncated === true &&
      existing != null &&
      existing.truncated !== true
    ) {
      continue;
    }
    byId.set(m.id, m);
  }
  return [...byId.values()].sort((a, b) => {
    const byTime = a.timestamp - b.timestamp;
    if (byTime !== 0) return byTime;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
}

function mergeTombstones(
  a: readonly Tombstone[],
  b: readonly Tombstone[],
  now: number,
): readonly Tombstone[] {
  const byKey = new Map<string, Tombstone>();
  for (const t of [...a, ...b]) {
    const key = `${t.kind}:${t.id}`;
    const existing = byKey.get(key);
    if (existing == null || t.deletedAt > existing.deletedAt) byKey.set(key, t);
  }
  return [...byKey.values()].filter(
    (t) => now - t.deletedAt < TOMBSTONE_TTL_MS,
  );
}

/**
 * The custom-folder assignment merges field-wise, not with the record: a
 * folder move deliberately doesn't bump `updatedAt` (it isn't chat
 * activity and must not resurface the chat in Recents), so it stamps
 * `folderUpdatedAt` instead. Without this, any message activity on a
 * device holding a pre-move record would win the whole-record merge and
 * silently undo the move — chats visibly popping out of their folders.
 */
function mergeFolderAssignment<S extends SyncSessionLike>(
  winner: S,
  loser: S,
): S {
  if ((loser.folderUpdatedAt ?? 0) <= (winner.folderUpdatedAt ?? 0)) {
    return winner;
  }
  // Loser holds the fresher assignment — graft it onto the winner. The
  // spread-then-delete dance (instead of `customFolderId: undefined`)
  // keeps unfiled records free of the key, matching what the stores
  // persist.
  const grafted = {
    ...winner,
    folderUpdatedAt: loser.folderUpdatedAt,
    customFolderId: loser.customFolderId,
  };
  if (loser.customFolderId == null) {
    delete (grafted as { customFolderId?: string }).customFolderId;
  }
  return grafted;
}

/**
 * A "clear chat" also merges field-wise, for the same reason as the folder
 * assignment: clearing doesn't bump `updatedAt` (it isn't chat activity),
 * so a device that kept chatting would win the whole-record merge and drop
 * the `clearedAt` stamp — resurrecting the cleared messages everywhere.
 * Max wins: a clear is monotonic, and messages sent AFTER the clear still
 * survive the hydration filter (`timestamp > clearedAt`).
 */
function mergeClearedAt<S extends SyncSessionLike>(winner: S, loser: S): S {
  if ((loser.clearedAt ?? 0) <= (winner.clearedAt ?? 0)) return winner;
  return { ...winner, clearedAt: loser.clearedAt };
}

/**
 * Merge two index documents. Sessions are unioned by `sessionId` with the
 * fresher `updatedAt` winning (the folder assignment and `clearedAt` merge
 * field-wise — see `mergeFolderAssignment` / `mergeClearedAt`); folders are
 * unioned by `id` (local wins on conflict — folders carry no update
 * timestamp). A tombstone suppresses an entry unless the entry was updated
 * after the deletion.
 */
export function mergeIndex<S extends SyncSessionLike, F extends SyncFolderLike>(
  local: SyncIndexDoc<S, F>,
  remote: SyncIndexDoc<S, F>,
  now: number = Date.now(),
): SyncIndexDoc<S, F> {
  const deleted = mergeTombstones(local.deleted, remote.deleted, now);
  const deletedAtOf = (kind: Tombstone['kind'], id: string): number | null => {
    const t = deleted.find((d) => d.kind === kind && d.id === id);
    return t == null ? null : t.deletedAt;
  };

  const sessionsById = new Map<string, S>();
  for (const s of remote.sessions) sessionsById.set(s.sessionId, s);
  for (const s of local.sessions) {
    const existing = sessionsById.get(s.sessionId);
    if (existing == null) {
      sessionsById.set(s.sessionId, s);
      continue;
    }
    const winner = s.updatedAt >= existing.updatedAt ? s : existing;
    const loser = winner === s ? existing : s;
    sessionsById.set(
      s.sessionId,
      mergeClearedAt(mergeFolderAssignment(winner, loser), loser),
    );
  }
  const sessions = [...sessionsById.values()]
    .filter((s) => {
      const deletedAt = deletedAtOf('session', s.sessionId);
      return deletedAt == null || s.updatedAt > deletedAt;
    })
    .sort((a, b) => b.updatedAt - a.updatedAt);

  const foldersById = new Map<string, F>();
  for (const f of remote.folders) foldersById.set(f.id, f);
  for (const f of local.folders) foldersById.set(f.id, f);
  const folders = [...foldersById.values()]
    .filter((f) => deletedAtOf('folder', f.id) == null)
    .sort((a, b) => a.createdAt - b.createdAt);

  return { sessions, folders, deleted };
}

/** An empty index document — the identity element for `mergeIndex`. */
export function emptyIndexDoc<
  S extends SyncSessionLike,
  F extends SyncFolderLike,
>(): SyncIndexDoc<S, F> {
  return { sessions: [], folders: [], deleted: [] };
}
